import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["inputField", "latitudeField", "longitudeField", "venueNameField", "clearBtn"]

  static values = {
    location: String,
  }

  connect() {
    if (typeof google === 'undefined') {
      window.addEventListener('load', () => {
        this.initializeAutocomplete()
      });
    } else {
      this.initializeAutocomplete();
    }
    // Show/hide the clear (×) button based on whether the field has text
    // (it starts populated with the "Venue to be announced" placeholder from
    // AI onboarding, which the organizer needs an easy way to wipe).
    this.toggleClear();
    this.inputFieldTarget.addEventListener("input", () => this.toggleClear());
  }

  toggleClear() {
    if (!this.hasClearBtnTarget) return;
    this.clearBtnTarget.hidden = this.inputFieldTarget.value.trim() === "";
  }

  clear() {
    this.inputFieldTarget.value = "";
    if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = "";
    if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = "";
    this.userSelectedAddress = null;
    this.toggleClear();
    this.inputFieldTarget.focus();
  }

  initializeAutocomplete() {
    try {
      this.autocomplete = new google.maps.places.Autocomplete(this.inputFieldTarget, {
        types: ['address']
      });

      // Picking a Places suggestion (or pressing Enter in the field) otherwise
      // bubbles an Enter keypress to the form and submits it. Swallow Enter on
      // the address input so selecting a suggestion never submits the form.
      this.inputFieldTarget.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });

      this.userSelectedAddress = null;

      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();
        
        if (place.formatted_address && place.geometry) {
          this.inputFieldTarget.value = place.formatted_address;
          this.userSelectedAddress = this.inputFieldTarget.value;
          this.setLocationFields(place);
          this.setVenueNameFromPlace(place);
        }
      });

      this.inputFieldTarget.addEventListener('blur', () => {
        const place = this.autocomplete.getPlace();
        const inputValue = this.inputFieldTarget.value.trim();
        
        if ((!place || !place.formatted_address || !place.geometry) && inputValue) {
          if (inputValue !== this.locationValue && inputValue !== this.userSelectedAddress) {
            this.inputFieldTarget.value = this.locationValue || '';
            if (this.hasLatitudeFieldTarget) this.latitudeFieldTarget.value = '';
            if (this.hasLongitudeFieldTarget) this.longitudeFieldTarget.value = '';
            // Clear venue name only if it was likely auto-populated (check if it matches common establishment patterns)
            if (this.hasVenueNameFieldTarget && this.venueNameFieldTarget.value.trim()) {
              this.venueNameFieldTarget.value = '';
            }
          }
        }
      });
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  }

  setPreEnteredLocation() {
    this.inputFieldTarget.value = this.locationValue || "";
  }

  setLocationFields(place) {
    this.latitudeFieldTarget.value = place.geometry.location.lat();
    this.longitudeFieldTarget.value = place.geometry.location.lng();
  }

  setVenueNameFromPlace(place) {
    if (this.hasVenueNameFieldTarget && !this.venueNameFieldTarget.value.trim()) {
      // Try to get establishment name from place.name or address components
      let venueName = '';
      
      if (place.name && place.name !== place.formatted_address) {
        venueName = place.name;
      } else if (place.address_components) {
        // Look for establishment, point_of_interest, or premise
        const nameComponent = place.address_components.find(component => 
          component.types.includes('establishment') || 
          component.types.includes('point_of_interest') ||
          component.types.includes('premise')
        );
        if (nameComponent) {
          venueName = nameComponent.long_name;
        }
      }
      
      if (venueName) {
        this.venueNameFieldTarget.value = venueName;
      }
    }
  }
}
